import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

/** How long a card opened by hover waits after the pointer leaves its marker
 *  or the card, so the pointer can cross the gap between them. */
export const HOVER_GRACE_MS = 150;
const GAP = 8;
const GUTTER = 16;
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface OpenCard {
  key: string;
  anchor: HTMLElement;
  /** Opened by a tap, click or focus: it stays until a tap elsewhere. */
  pinned: boolean;
}

/** The focusable element after `anchor` in the page, outside `skip`. */
const focusAfter = (anchor: HTMLElement, skip: HTMLElement | null) => {
  const all = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !skip?.contains(el) && !el.closest('[aria-hidden="true"]'),
  );
  all[all.indexOf(anchor) + 1]?.focus();
};

/** A card for chart markers (pips, campaign lanes): opened by hover on a
 *  pointer with a short grace so the pointer can move into it, or pinned by
 *  a tap, click or focus. Tab from the marker moves into the card's rows,
 *  Escape goes back to the marker, Tab past the last row on to the page. */
export const useMarkerCard = () => {
  const [open, setOpen] = useState<OpenCard | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  // Focus sent back to a marker by code (Escape, a closed drawer) opens nothing.
  const quietFocus = useRef(false);
  const cardId = useId();

  const cancelClose = useCallback(() => window.clearTimeout(closeTimer.current), []);
  const close = useCallback(() => {
    cancelClose();
    setOpen(null);
  }, [cancelClose]);
  const closeSoon = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(
      () => setOpen((current) => (current && !current.pinned ? null : current)),
      HOVER_GRACE_MS,
    );
  }, [cancelClose]);
  useEffect(() => cancelClose, [cancelClose]);

  /** Focus a marker without opening its card. */
  const focusQuietly = useCallback((anchor: HTMLElement) => {
    quietFocus.current = true;
    anchor.focus();
    quietFocus.current = false;
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: globalThis.PointerEvent) => {
      const target = e.target instanceof Node ? e.target : null;
      if (target && (open.anchor.contains(target) || cardRef.current?.contains(target))) return;
      close();
    };
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  const triggerProps = (key: string) => {
    const isOpen = open?.key === key;
    return {
      "aria-expanded": isOpen,
      "aria-controls": isOpen ? cardId : undefined,
      onPointerEnter: (e: PointerEvent<HTMLElement>) => {
        if (e.pointerType !== "mouse") return;
        cancelClose();
        if (!open?.pinned) setOpen({ key, anchor: e.currentTarget, pinned: false });
      },
      onPointerLeave: (e: PointerEvent<HTMLElement>) => {
        if (e.pointerType === "mouse" && isOpen && !open?.pinned) closeSoon();
      },
      onFocus: (e: FocusEvent<HTMLElement>) => {
        if (quietFocus.current) return;
        cancelClose();
        setOpen({ key, anchor: e.currentTarget, pinned: true });
      },
      onBlur: (e: FocusEvent<HTMLElement>) => {
        // Focus to nowhere (Safari blurs a button on a click without focusing
        // the next) is left to the outside-tap handler, so a click on a row
        // still lands.
        const next = e.relatedTarget instanceof Node ? e.relatedTarget : null;
        if (isOpen && next && !cardRef.current?.contains(next)) close();
      },
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        cancelClose();
        setOpen({ key, anchor: e.currentTarget, pinned: true });
      },
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        if (e.key !== "Tab" || e.shiftKey || !isOpen) return;
        const first = cardRef.current?.querySelector<HTMLElement>(FOCUSABLE);
        if (!first) return;
        e.preventDefault();
        setOpen((current) => current && { ...current, pinned: true });
        first.focus();
      },
    };
  };

  const cardProps = {
    ref: cardRef,
    id: cardId,
    onPointerEnter: (e: PointerEvent<HTMLElement>) => {
      if (e.pointerType === "mouse") cancelClose();
    },
    onPointerLeave: (e: PointerEvent<HTMLElement>) => {
      if (e.pointerType === "mouse" && !open?.pinned) closeSoon();
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (!open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        focusQuietly(open.anchor);
        return;
      }
      if (e.key !== "Tab") return;
      const rows = Array.from(cardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      const at = rows.indexOf(e.target as HTMLElement);
      if (e.shiftKey && at === 0) {
        e.preventDefault();
        focusQuietly(open.anchor);
      } else if (!e.shiftKey && at === rows.length - 1) {
        e.preventDefault();
        close();
        focusAfter(open.anchor, cardRef.current);
      }
    },
    onBlur: (e: FocusEvent<HTMLElement>) => {
      const next = e.relatedTarget instanceof Node ? e.relatedTarget : null;
      if (!open || !next || cardRef.current?.contains(next) || open.anchor.contains(next)) return;
      close();
    },
  };

  return { open, close, focusQuietly, triggerProps, cardProps };
};

type CardProps = ReturnType<typeof useMarkerCard>["cardProps"];

/** The card itself, floating on the page so a panel's scroll box never
 *  clips it: above its marker, or below when the top of the screen is too
 *  near, kept inside the side gutters, and moved with its marker on scroll. */
export const MarkerCardPortal: React.FC<{
  anchor: HTMLElement;
  cardProps: CardProps;
  label: string;
  children: ReactNode;
}> = ({ anchor, cardProps, label, children }) => {
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const { ref } = cardProps;

  const place = useCallback(() => {
    const card = ref.current;
    if (!card) return;
    const box = anchor.getBoundingClientRect();
    const left = Math.min(
      Math.max(box.left + box.width / 2 - card.offsetWidth / 2, GUTTER),
      window.innerWidth - card.offsetWidth - GUTTER,
    );
    const above = box.top - GAP - card.offsetHeight;
    setPosition({ left, top: above >= GUTTER ? above : box.bottom + GAP });
  }, [anchor, ref]);

  useLayoutEffect(place, [place, children]);
  useEffect(() => {
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [place]);

  return createPortal(
    <div
      {...cardProps}
      role="group"
      aria-label={label}
      className="fixed z-50 w-[260px] max-w-[calc(100vw-32px)] rounded-xl bg-white p-[10px] shadow-lg"
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? "visible" : "hidden",
      }}
    >
      {children}
    </div>,
    document.body,
  );
};
